import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import type { PaymentStatus } from "@mandoob/shared";

import { useGetLedger, useGetPayments } from "@/api/billing";
import { Badge } from "@/components/ui/badge";
import { Currency } from "@/components/shared/currency";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/language";
import { formatDateMedium, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

const STATUS_KEYS: Record<PaymentStatus, string> = {
  Pending: "Mandoob:Payments.StatusPending",
  Paid: "Mandoob:Payments.StatusPaid",
  Failed: "Mandoob:Payments.StatusFailed",
  Expired: "Mandoob:Payments.StatusExpired",
};

const STATUS_STYLES: Record<PaymentStatus, string> = {
  Pending: "tw:bg-warning-soft tw:text-warning tw:border-transparent",
  Paid: "tw:bg-success-soft tw:text-success-foreground tw:border-transparent",
  Failed: "tw:bg-destructive-soft tw:text-destructive tw:border-transparent",
  Expired: "tw:bg-muted tw:text-muted-foreground tw:border-transparent",
};

const REASON_KEYS: Record<string, string> = {
  template_send: "Mandoob:Ledger.TemplateSend",
  chat_send: "Mandoob:Ledger.ChatSend",
  plan_purchase: "Mandoob:Ledger.PlanPurchase",
  refund: "Mandoob:Ledger.Refund",
  admin_adjust: "Mandoob:Ledger.AdminAdjust",
  welcome_bonus: "Mandoob:Ledger.WelcomeBonus",
  gift: "Mandoob:Ledger.Gift",
  expired: "Mandoob:Ledger.Expired",
};

function ListSkeleton() {
  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="tw:h-16 tw:w-full tw:rounded-xl" />
      ))}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <p className="tw:m-0 tw:rounded-xl tw:border tw:border-dashed tw:border-border tw:px-4 tw:py-10 tw:text-center tw:text-sm tw:text-muted-foreground">
      {text}
    </p>
  );
}

export function PaymentsHistoryPage() {
  const { t, dir, language } = useLanguage();
  // History is REVIEWING credits — always available, app payments flag or
  // not (only purchasing moves to the website).
  const paymentsQuery = useGetPayments({ MaxResultCount: 50 });
  const ledgerQuery = useGetLedger({ MaxResultCount: 100 });

  const payments = paymentsQuery.data?.items ?? [];
  const ledger = ledgerQuery.data?.items ?? [];

  return (
    <div dir={dir} className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
      <div className="tw:mx-auto tw:flex tw:w-full tw:flex-col tw:gap-4 tw:px-4 tw:pb-6 tw:pt-[calc(12px+env(safe-area-inset-top))] tw:md:max-w-3xl tw:md:px-8 tw:md:pt-6">
        <header className="tw:flex tw:items-center tw:gap-3">
          <Link
            to="/settings"
            aria-label={t("Mandoob:Back")}
            className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-muted-foreground tw:no-underline tw:transition-colors tw:hover:bg-muted"
          >
            <ArrowRight className="tw:size-5 tw:ltr:-scale-x-100" />
          </Link>
          <h1 className="tw:m-0 tw:text-lg tw:font-semibold">
            {t("Mandoob:Payments.Title")}
          </h1>
        </header>

        <Tabs defaultValue="payments">
          <TabsList className="tw:w-full">
            <TabsTrigger value="payments">
              {t("Mandoob:Payments.PurchasesTab")}
            </TabsTrigger>
            <TabsTrigger value="ledger">
              {t("Mandoob:Payments.LedgerTab")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="payments">
            {paymentsQuery.isLoading ? (
              <ListSkeleton />
            ) : payments.length === 0 ? (
              <EmptyState text={t("Mandoob:Payments.NoPayments")} />
            ) : (
              <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-2 tw:p-0">
                {payments.map((payment) => (
                  <li
                    key={payment.id}
                    className="tw:flex tw:items-center tw:gap-3 tw:rounded-xl tw:border tw:border-border tw:bg-card tw:p-3.5"
                  >
                    <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-0.5">
                      <span className="tw:truncate tw:text-base tw:font-medium">
                        {language === "ar"
                          ? payment.planNameAr
                          : payment.planNameEn}
                      </span>
                      <span className="tw:text-xs tw:text-muted-foreground">
                        {formatDateMedium(payment.creationTime, language)} ·{" "}
                        {t(
                          "Mandoob:Payments.CreditsCount",
                          formatNumber(payment.credits, language),
                        )}
                      </span>
                    </div>
                    <div className="tw:flex tw:shrink-0 tw:flex-col tw:items-end tw:gap-1.5">
                      <span className="tw:inline-flex tw:items-center tw:gap-1 tw:text-base tw:font-semibold tw:tabular-nums">
                        {formatNumber(payment.amountHalalas / 100, language, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                        <Currency
                          currency={payment.currency}
                          className="tw:size-4"
                        />
                      </span>
                      <Badge
                        className={cn(
                          "tw:px-2 tw:py-0.5 tw:text-xs",
                          STATUS_STYLES[payment.status],
                        )}
                      >
                        {t(STATUS_KEYS[payment.status])}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="ledger">
            {ledgerQuery.isLoading ? (
              <ListSkeleton />
            ) : ledger.length === 0 ? (
              <EmptyState text={t("Mandoob:Payments.NoLedger")} />
            ) : (
              <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-2 tw:p-0">
                {ledger.map((entry) => (
                  <li
                    key={entry.id}
                    className="tw:flex tw:items-center tw:gap-3 tw:rounded-xl tw:border tw:border-border tw:bg-card tw:p-3.5"
                  >
                    <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-0.5">
                      <span className="tw:text-base tw:font-medium">
                        {t(REASON_KEYS[entry.reason] ?? entry.reason)}
                      </span>
                      <span className="tw:text-xs tw:text-muted-foreground">
                        {formatDateMedium(entry.creationTime, language)}
                      </span>
                    </div>
                    <div className="tw:flex tw:shrink-0 tw:flex-col tw:items-end tw:gap-0.5">
                      <span
                        className={cn(
                          "tw:text-base tw:font-semibold tw:tabular-nums",
                          entry.amount > 0
                            ? "tw:text-success-foreground"
                            : "tw:text-foreground",
                        )}
                        dir="ltr"
                        style={{ unicodeBidi: "isolate" }}
                      >
                        {entry.amount > 0 ? "+" : ""}
                        {formatNumber(entry.amount, language)}
                      </span>
                      <span className="tw:text-xs tw:text-muted-foreground">
                        {t(
                          "Mandoob:Ledger.BalanceAfter",
                          formatNumber(entry.balanceAfter, language),
                        )}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
